import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import portalApi from '../lib/portalApi';
import { toast } from 'sonner';
import {
  Shield, FileText, DollarSign, Calendar, LogOut, 
  CheckCircle2, AlertCircle, Clock, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';

export const ClientPortalDashboard = () => {
  const navigate = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractorName, setContractorName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    // Verificar sesión
    const sessionToken = localStorage.getItem('portal_session');
    const name = localStorage.getItem('portal_contractor_name');
    const expires = localStorage.getItem('portal_expires_at');
    
    console.log('Portal session check:', { sessionToken: !!sessionToken, name, expires });
    
    if (!sessionToken) {
      toast.error('Sesión no encontrada. Por favor inicia sesión.');
      // No redirigir, solo mostrar error
      setLoading(false);
      return;
    }
    
    setContractorName(name || 'Cliente');
    setExpiresAt(expires || '');
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const sessionToken = localStorage.getItem('portal_session');
      
      // Obtener resumen y pólizas en paralelo
      // El interceptor de portalApi automáticamente agrega el header Authorization
      const [summaryResponse, policiesResponse] = await Promise.all([
        portalApi.get('/client-portal/summary'),
        portalApi.get('/client-portal/policies')
      ]);
      
      setSummary(summaryResponse.data);
      setPolicies(policiesResponse.data);
      
      // Actualizar nombre con el alias más reciente del backend
      if (summaryResponse.data.contractor_name) {
        setContractorName(summaryResponse.data.contractor_name);
        localStorage.setItem('portal_contractor_name', summaryResponse.data.contractor_name);
      }
      
    } catch (error) {
      console.error('Error fetching portal data:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sesión expirada. Por favor inicia sesión nuevamente.');
        handleLogout();
      } else {
        toast.error('Error al cargar información');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_session');
    localStorage.removeItem('portal_contractor_name');
    localStorage.removeItem('portal_expires_at');
    localStorage.removeItem('portal_access_id');
    toast.success('Sesión cerrada');
    // Redirigir a la página principal en lugar de login
    window.location.href = '/';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Formato DD/MM/YYYY (usado en México)
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Formato YYYY-MM-DD (ISO)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Si tiene componente de tiempo, usar el método estándar
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'En Vigor':
        return 'default';
      case 'Por Vencer':
        return 'warning';
      case 'Vencida':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando tu información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Portal de Clientes</h1>
                <p className="text-sm text-slate-600">Powered by Asístelo</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <User className="h-4 w-4" />
                  <span className="font-medium">{contractorName}</span>
                </div>
                {expiresAt && (
                  <p className="text-xs text-slate-500">
                    Acceso válido hasta: {formatDate(expiresAt)}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido Principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bienvenida */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            Hola, {contractorName}
          </h2>
          <p className="text-slate-600">
            Aquí puedes consultar toda la información de tus pólizas de seguro
          </p>
        </div>

        {/* Stats Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Total Pólizas
                </CardTitle>
                <FileText className="h-4 w-4 text-slate-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  {summary.total_policies}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Pólizas Activas
                </CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {summary.active_policies}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Suma Asegurada Total
                </CardTitle>
                <Shield className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {formatCurrency(summary.total_insured_amount)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Prima Anual Total
                </CardTitle>
                <DollarSign className="h-4 w-4 text-slate-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {formatCurrency(summary.total_annual_premium)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerta de renovaciones próximas */}
        {summary && summary.upcoming_renewals > 0 && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Atención:</strong> Tienes {summary.upcoming_renewals} póliza(s) 
              que vence(n) en los próximos 30 días. Contacta a tu asesor para renovarla(s).
            </AlertDescription>
          </Alert>
        )}

        {/* Tabla de Pólizas */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Mis Pólizas de Seguro</CardTitle>
          </CardHeader>
          <CardContent>
            {policies.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No se encontraron pólizas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número de Póliza</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Aseguradora</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-right">Suma Asegurada</TableHead>
                      <TableHead className="text-right">Prima Anual</TableHead>
                      <TableHead className="text-center">Termina tu cobertura</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {policies.map((policy) => (
                      <TableRow key={policy.id}>
                        <TableCell className="font-medium">
                          {policy.policy_number}
                        </TableCell>
                        <TableCell>{policy.policy_type}</TableCell>
                        <TableCell>{policy.insurer}</TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={getStatusVariant(policy.status)}
                            className={
                              policy.status === 'En Vigor' ? 'bg-green-600' : ''
                            }
                          >
                            {policy.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(policy.insured_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {policy.annual_premium 
                            ? formatCurrency(policy.annual_premium)
                            : 'N/A'}
                        </TableCell>
                        <TableCell className="text-center">
                          {policy.end_date ? (
                            <div className="flex items-center justify-center gap-1">
                              <Calendar className="h-3 w-3 text-slate-400" />
                              <span className="text-sm">
                                {formatDate(policy.end_date)}
                              </span>
                            </div>
                          ) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info adicional */}
        <div className="mt-8 text-center">
          <Alert className="inline-block bg-blue-50 border-blue-200">
            <AlertDescription className="text-blue-800 text-sm">
              <strong>¿Necesitas ayuda?</strong> Contacta a tu asesor de seguros para cualquier 
              consulta sobre tus pólizas o renovaciones.
            </AlertDescription>
          </Alert>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-slate-500">
            © 2026 Asístelo | Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ClientPortalDashboard;
